import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { contractService } from '../../services/contractService';
import { harvestService } from '../../services/harvestService';
import { parseHarvestEstimate, estimateToContractData } from '../../utils/harvestEstimateParser';
import type { Contract, Deliverable, Milestone, Payment, Clause, ContractTemplate } from '@shared/types';
import { DetailsStep } from './DetailsStep';
import { TemplateStep } from './TemplateStep';
import { ClausesStep } from './ClausesStep';
import { DeliverablesStep } from './DeliverablesStep';
import { MilestonesStep } from './MilestonesStep';
import { PaymentsStep } from './PaymentsStep';
import { ScopePricingStep } from './ScopePricingStep';
import { PreviewStep } from './PreviewStep';
import { HarvestImportModal } from './HarvestImportModal';
import { useTemplates } from '../../hooks/useTemplates';

interface ContractWizardProps {
  existingContract?: Contract;
  isEditing?: boolean;
}

export function ContractWizard({ existingContract, isEditing = false }: ContractWizardProps = {}) {
  const navigate = useNavigate();
  const { templates } = useTemplates();
  const [currentStep, setCurrentStep] = useState(isEditing ? 1 : 0); // Start at Details step when editing
  const [saving, setSaving] = useState(false);
  const [showHarvestImport, setShowHarvestImport] = useState(false);
  const [selectedClientHarvestId, setSelectedClientHarvestId] = useState<string | undefined>();
  
  // Calculate default expiration date (30 days from today)
  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };
  
  const [formData, setFormData] = useState<Partial<Contract> & { selectedTemplate?: ContractTemplate }>({
    clientName: '',
    executiveSummary: '',
    deliverables: [],
    milestones: [],
    payments: [],
    clauses: [],
    expirationDate: getDefaultExpirationDate(),
    paymentScheduleType: 'one-time',
  });

  // Pre-populate form data when editing
  useEffect(() => {
    if (existingContract && templates.length > 0) {
      const template = templates.find(t => t.id === existingContract.templateId);
      setFormData({
        ...existingContract,
        selectedTemplate: template,
      });
    }
  }, [existingContract, templates]);

  // Dynamically build steps based on selected template
  const steps = useMemo(() => {
    const template = formData.selectedTemplate;
    const baseSteps = ['Template'];
    
    if (!template) return baseSteps; // Only show template selection until template is chosen

    // Add steps based on template configuration
    baseSteps.push('Details');
    
    // Use combined Scope & Pricing step for templates with deliverables or payments
    if (template.hasDeliverables || template.hasPaymentSchedule) {
      baseSteps.push('Scope & Pricing');
    }
    
    baseSteps.push('Preview');
    
    return baseSteps;
  }, [formData.selectedTemplate]);

  const updateFormData = (updates: Partial<Contract>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };
  
  const handleHarvestImport = (estimate: any) => {
    console.log('🔍 Importing Harvest estimate:', estimate);
    
    // Parse the estimate using intelligent parser
    const parsed = parseHarvestEstimate(estimate);
    console.log('📊 Parsed estimate:', parsed);
    
    // Convert to contract form data
    const contractData = estimateToContractData(estimate, parsed);
    console.log('📝 Contract data:', contractData);
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      ...contractData,
    }));
    
    const details = [
      parsed.deliverables.length > 0 && `${parsed.deliverables.length} deliverable(s)`,
      parsed.tasks.length > 0 && `${parsed.tasks.length} task(s)`,
      parsed.paymentSchedule && `${parsed.paymentSchedule.breakdown.length} payment(s)`,
    ].filter(Boolean).join(', ');
    
    toast.success(`Estimate imported: ${details}`);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepName: string) => {
    const stepIndex = steps.indexOf(stepName);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
    }
  };

  const canProceed = () => {
    const stepName = steps[currentStep];
    
    // Validate template is selected before proceeding from Template step
    if (stepName === 'Template' && !formData.selectedTemplate) {
      toast.error('Please select a template to continue');
      return false;
    }
    
    // Validate required fields in Details step
    if (stepName === 'Details') {
      // Only validate project name for SOW templates (hasScope)
      if (formData.selectedTemplate?.hasScope && !formData.title?.trim()) {
        toast.error('Please enter a project name');
        return false;
      }
      if (!formData.clientName?.trim()) {
        toast.error('Please select a client');
        return false;
      }
      if (!formData.expirationDate) {
        toast.error('Please set an expiration date');
        return false;
      }
    }
    
    // Validate Scope & Pricing step
    if (stepName === 'Scope & Pricing') {
      if (!formData.serviceCost || formData.serviceCost <= 0) {
        toast.error('Please enter a service cost');
        return false;
      }
      if (formData.selectedTemplate?.hasDeliverables && (!formData.deliverables || formData.deliverables.length === 0)) {
        toast.error('Please add at least one deliverable');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    const toastId = toast.loading(isEditing ? 'Updating contract...' : 'Creating contract...');
    
    try {
      let contract;
      if (isEditing && existingContract) {
        contract = await contractService.updateContract(existingContract.id, formData);
        toast.success('Contract updated successfully!', { id: toastId });
      } else {
        contract = await contractService.createContract(formData);
        toast.success('Contract created successfully!', { id: toastId });
      }
      navigate(`/contracts/${contract.id}`);
    } catch (error) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} contract:`, error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} contract. Please try again.`, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    const stepName = steps[currentStep];
    
    switch (stepName) {
      case 'Template':
        return <TemplateStep data={formData} onUpdate={updateFormData} />;
      case 'Details':
        return (
          <DetailsStep 
            data={formData} 
            onUpdate={updateFormData}
            template={formData.selectedTemplate}
            onClientSelect={(harvestId) => setSelectedClientHarvestId(harvestId)}
            onImportFromHarvest={() => setShowHarvestImport(true)}
          />
        );
      case 'Scope & Pricing':
        return <ScopePricingStep data={formData} onUpdate={updateFormData} template={formData.selectedTemplate} />;
      case 'Preview':
        return <PreviewStep data={formData} template={formData.selectedTemplate} onEditStep={goToStep} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{isEditing ? 'Edit Contract' : 'Create New Contract'}</h1>
          <p className="mt-2 text-gray-600">{isEditing ? 'Update your contract details below' : 'Follow the steps below to create your contract'}</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center">
                {/* Step Circle */}
                <div className="flex flex-col items-center">
                  <div
                    className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-200 ${
                      index < currentStep
                        ? 'bg-primary-600 border-primary-600'
                        : index === currentStep
                        ? 'bg-primary-600 border-primary-600 ring-4 ring-primary-100'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {index < currentStep ? (
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span
                        className={`text-lg font-semibold ${
                          index === currentStep ? 'text-white' : 'text-gray-500'
                        }`}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 text-center">
                    <p
                      className={`text-sm font-medium whitespace-nowrap ${
                        index <= currentStep ? 'text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {step}
                    </p>
                  </div>
                </div>
                
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="w-24 h-0.5 mx-4 mb-8">
                    <div
                      className={`h-full transition-all duration-300 ${
                        index < currentStep ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white shadow-sm rounded-lg p-8">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/contracts')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            {currentStep === steps.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Contract' : 'Create Contract')}
              </button>
            ) : (
              <button
                onClick={() => canProceed() && nextStep()}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Harvest Import Modal */}
      <HarvestImportModal 
        isOpen={showHarvestImport}
        onClose={() => setShowHarvestImport(false)}
        onImport={handleHarvestImport}
        harvestClientId={selectedClientHarvestId}
      />
    </div>
  );
}

